import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addResidentGroupFailure,
  addResidentGroupSuccess,
  deleteResidentGroupFailure,
  deleteResidentGroupSuccess,
  editResidentGroupFailure,
  editResidentGroupSuccess,
  fetchResidentGroupFailure,
  fetchResidentGroupItemFailure,
  fetchResidentGroupItemSuccess,
  fetchResidentGroupSuccess,
  searchResidentGroupFailure,
  searchResidentGroupSuccess,
} from "./actions";
import {
  ADD_RESIDENT_GROUP,
  DELETE_RESIDENT_GROUP,
  EDIT_RESIDENT_GROUP,
  FETCH_RESIDENT_GROUP,
  FETCH_RESIDENT_GROUP_ITEM,
  SEARCH_RESIDENT_GROUP,
} from "./constants";
import { searchResidentGroupQuerySchema } from "./searchQuerySchema";

const SETTINGS_PATH = "/d/r/settings/resident-groups";

const normalizeResidentGroupList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export function* searchResidentGroupSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const raw = {
      location_id: payload?.location_id,
      search_term: payload?.search_term?.trim?.()
        ? payload.search_term.trim()
        : undefined,
      all: payload?.all !== false,
      page: payload?.page,
    };
    const queryPayload = searchResidentGroupQuerySchema.validateSync(raw, {
      stripUnknown: true,
    });
    const query = QueryString.stringify(queryPayload);
    const { data } = yield call(api(token).get, `/api/resident-group?${query}`);
    yield put(searchResidentGroupSuccess(normalizeResidentGroupList(data)));
  } catch (e) {
    yield put(searchResidentGroupFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else if (e?.name === "ValidationError") {
      yield put(errorAlert(e.message || "Invalid resident group search"));
    } else yield put(errorAlert("Failed to search resident groups"));
  }
}

export function* fetchResidentGroupSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `/api/resident-group?${query}`);
    const result = Array.isArray(data)
      ? { data, total: data.length, current_page: payload?.page ?? 1 }
      : { ...data, current_page: payload?.page ?? data?.current_page ?? 1 };
    yield put(fetchResidentGroupSuccess(result));
  } catch (e) {
    yield put(fetchResidentGroupFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch resident groups"));
  }
}

export function* addResidentGroupSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, onSuccess } = payload || {};
  try {
    const response = yield call(
      api(token).post,
      `/api/resident-group/add`,
      data,
    );
    if (response) {
      yield put(addResidentGroupSuccess());
      yield put(successAlert("Resident group created successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(addResidentGroupFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to create resident group"));
  }
}

export function* fetchResidentGroupItemSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id =
    typeof payload === "string" ? payload : (payload?.uuid ?? payload?.id);
  try {
    const { data } = yield call(api(token).get, `/api/resident-group/${id}`);
    yield put(fetchResidentGroupItemSuccess(data?.data ?? data));
  } catch (e) {
    yield put(fetchResidentGroupItemFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch resident group"));
  }
}

export function* editResidentGroupSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, id, uuid, onSuccess } = payload || {};
  const groupId = id ?? uuid;
  try {
    const response = yield call(
      api(token).post,
      `/api/resident-group/edit/${groupId}`,
      data,
    );
    if (response) {
      yield put(editResidentGroupSuccess());
      yield put(successAlert("Resident group updated successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(editResidentGroupFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to update resident group"));
  }
}

export function* deleteResidentGroupSaga({ payload, onDelete }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id = payload?.id ?? payload?.uuid;
  try {
    const data = yield call(api(token).delete, `/api/resident-group/${id}`);
    if (data) {
      yield put(deleteResidentGroupSuccess());
      yield put(successAlert("Resident group deleted successfully"));
      typeof onDelete === "function" && onDelete();
    }
  } catch (e) {
    yield put(deleteResidentGroupFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to delete resident group"));
  }
}

export default function* ResidentGroupSaga() {
  yield all([
    takeEvery(SEARCH_RESIDENT_GROUP, searchResidentGroupSaga),
    takeEvery(FETCH_RESIDENT_GROUP, fetchResidentGroupSaga),
    takeEvery(ADD_RESIDENT_GROUP, addResidentGroupSaga),
    takeEvery(FETCH_RESIDENT_GROUP_ITEM, fetchResidentGroupItemSaga),
    takeEvery(EDIT_RESIDENT_GROUP, editResidentGroupSaga),
    takeEvery(DELETE_RESIDENT_GROUP, deleteResidentGroupSaga),
  ]);
}
